import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  useTheme,
  CircularProgress
} from '@mui/material';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import Button from '@/components/Common/Button';
import Input from '@/components/Common/Input';
import { FiUpload, FiTrash2, FiPlus, FiDownload } from 'react-icons/fi';
import {
  useCreateOrUpdateAgencyOnboardingForm,
  useDeleteOnboardingFile,
  useUpdatePublicOnboardingForm
} from '@/apis/agency/businessOnboarding/hooks';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';

export default function AdditionalInfo() {
  const theme = useTheme();
  const { control, watch, setValue } = useFormContext();
  const isBusinessOnboardingStage2 = location.pathname.includes('/business-onboarding/stage-2');

  const currentFormId = Cookies.get('onboarding_id');

  const { mutate: updateAgency, isPending: isAgencyUpdating } = useCreateOrUpdateAgencyOnboardingForm(false);
  const { mutate: updatePublic, isPending: isPublicUpdating } = useUpdatePublicOnboardingForm();
  const { deleteFile } = useDeleteOnboardingFile();
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  const isUploading = isAgencyUpdating || isPublicUpdating;

  // Sync upload status with form context for global footer tracking
  useEffect(() => {
    setValue('isUploadingDoc', isUploading);
    if (!isUploading) {
      setUploadingIndex(null);
    }
  }, [isUploading, setValue]);

  const updateOnboardingForm = isBusinessOnboardingStage2
    ? (payload: FormData, options?: any) => currentFormId && updatePublic({ formId: currentFormId, payload }, options)
    : (payload: FormData, options?: any) => updateAgency(payload, options);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additional_documents_attributes'
  });

  return (
    <Box display='flex' flexDirection='column' gap={3}>
      {/* NOTE SECTION */}
      <Paper sx={{ border: '1px solid #EAEAEA', borderRadius: 2, p: { xs: 2, md: 4 } }}>
        <Typography fontWeight={600} fontSize={18}>
          Note
        </Typography>

        <Typography variant='body2' color='text.secondary' mt={1} mb={2}>
          Share any additional business details, special requirements, or instructions that may help us set things up
          for you.
        </Typography>

        <Controller
          control={control}
          name='additional_note'
          render={({ field }) => (
            <textarea
              {...field}
              placeholder='e.g: We prefer a friendly, professional tone with customers. If someone requests an exception to our policies, please collect their details for our team to review.'
              rows={5}
              style={{
                width: '100%',
                borderRadius: 8,
                border: '1px solid #E5E7EB',
                padding: '12px',
                fontSize: '14px',
                outline: 'none',
                resize: 'none'
              }}
            />
          )}
        />
      </Paper>

      {/* ✅ UPDATED DOCUMENT UPLOAD (FIELD ARRAY LIKE RECORDINGS) */}
      <Paper sx={{ border: '1px solid #EAEAEA', borderRadius: 2, p: 4 }}>
        <Typography fontWeight={600} fontSize={18}>
          Upload Documents
        </Typography>

        <Typography variant='body2' color='text.secondary' mt={1}>
          Upload documents such as guides, policies, or FAQs to help train your AI agent. These files are added to the
          agent’s knowledge base so it can provide accurate responses to callers.
        </Typography>

        {/* LIST */}
        <Box mt={3} display='flex' flexDirection='column' gap={3}>
          {fields.map((field, index) => (
            <Paper key={field.id} variant='outlined' sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              {/* HEADER ROW */}
              <Box display='flex' justifyContent='space-between'>
                {/* NUMBER */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#E3F2FD',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600
                  }}
                >
                  {index + 1}
                </Box>

                {/* DELETE */}
                <Box display='flex' alignItems='center' gap={1}>
                  {watch(`additional_documents_attributes.${index}.file_url`) && (
                    <IconButton
                      onClick={() => {
                        window.open(watch(`additional_documents_attributes.${index}.file_url`), '_blank');
                      }}
                    >
                      <FiDownload size={20} color='#2563eb' />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => {
                      const docId = watch(`additional_documents_attributes.${index}.id`);
                      remove(index);
                      if (docId) {
                        deleteFile(docId, 'additional_documents_attributes');
                      }
                    }}
                  >
                    <FiTrash2 color='#E53935' />
                  </IconButton>
                </Box>
              </Box>

              {/* FILE INPUT */}
              <Box mt={2}>
                <Controller
                  control={control}
                  name={`additional_documents_attributes.${index}.file`}
                  render={({ field: { onChange } }) => {
                    return (
                      <>
                        <input
                          type='file'
                          hidden
                          id={`doc-file-${index}`}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            setUploadingIndex(index);
                            // ✅ Store the human-readable filename in a separate field for the UI
                            setValue(`additional_documents_attributes.${index}.file_name`, file.name);

                            const docId = watch(`additional_documents_attributes.${index}.id`);

                            // 🔥 Instant upload
                            const fd = new FormData();
                            if (docId) {
                              fd.append('onboarding_form[additional_documents_attributes][][id]', docId);
                            }
                            fd.append('onboarding_form[additional_documents_attributes][][file]', file);

                            updateOnboardingForm(fd, {
                              onSuccess: (data: any) => {
                                const docs = data?.additional_documents_attributes || [];
                                const uploadedDoc = docs[index];
                                if (uploadedDoc) {
                                  setValue(`additional_documents_attributes.${index}.id`, uploadedDoc.id);
                                  setValue(`additional_documents_attributes.${index}.file_url`, uploadedDoc.file_url);
                                  // Update 'file' with the signed blob ID for the backend
                                  onChange(uploadedDoc.file);
                                }
                                setUploadingIndex(null);
                              },
                              onError: () => setUploadingIndex(null)
                            });
                          }}
                        />

                        <label htmlFor={`doc-file-${index}`}>
                          <Paper
                            variant='outlined'
                            sx={{
                              p: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              cursor: 'pointer'
                            }}
                          >
                            {uploadingIndex === index ? <CircularProgress size={20} /> : <FiUpload size={18} />}
                            <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                              {watch(`additional_documents_attributes.${index}.file_name`) ||
                                (watch(`additional_documents_attributes.${index}.file_url`)
                                  ? watch(`additional_documents_attributes.${index}.file_url`)
                                      .split('/')
                                      .pop()
                                      .split('?')[0]
                                  : 'Upload document')}
                            </Typography>
                          </Paper>
                        </label>
                      </>
                    );
                  }}
                />
              </Box>

              {/* DESCRIPTION */}
              <Box mt={2}>
                <Input
                  name={`additional_documents_attributes.${index}.description`}
                  control={control}
                  label='Description'
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  placeholder='e.g., SOP for handling customer calls, Refund policy...'
                />
              </Box>
            </Paper>
          ))}
        </Box>

        {/* ADD BUTTON */}
        <Box mt={3}>
          <Button
            variant='outlined'
            startIcon={<FiPlus />}
            sx={{ borderRadius: 20 }}
            onClick={() =>
              append({
                file: null,
                description: ''
              })
            }
          >
            Add Document
          </Button>
        </Box>
      </Paper>

      {/* CONFIRMATION */}
      {isBusinessOnboardingStage2 && (
        <Paper
          sx={{
            border: '1px solid #E5E7EB',
            borderRadius: 2,
            p: 3,
            background: theme.palette.primary.light
          }}
        >
          <Controller
            control={control}
            name='confirmation'
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={
                  <Box>
                    <Typography fontSize={14} fontWeight={500}>
                      I have filled in the information to the best of my knowledge and you can start building the Agent
                    </Typography>

                    <Typography fontSize={12} color='text.secondary' mt={0.5}>
                      By checking this box, you confirm that the details provided are accurate to the best of your
                      knowledge. Our team will review your submission and begin building your AI agent accordingly.
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Paper>
      )}
    </Box>
  );
}
